import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs/internal/Observable';
import {filter, map, share} from 'rxjs/operators';
import {environment} from '../../../environments/environment';

import {CatalogItemIcon, GiftCardImage, LoyaltyProgram, Merchant, MerchantMobileApp, MobileAppHomepageTile, MobileAppMenuTab, RewardTiers} from '../models/restaurant.model';
import { CartStore } from '../stores/cart.store';
import {dataToCatalogItemIcon, dataToMerchantGiftCardImage, dataToMerchantInfo, dataToMerchantMobileAppInfo, dataToMobileAppHomepageTile, dataToMobileAppMenuTab, dataToNotification, dataToPromos} from '../dto/merchant.dto';
import { dataToLoyaltyProgram } from '../dto/restaurant.dto';

@Injectable()
export class MerchantService {

  constructor(private http: HttpClient,
   private cartStore: CartStore) {
  }

  public getMerchants(): Observable<Merchant[]> {
    const apiURL = `${environment.apiUrl}merchants`;
    return this.http.get(apiURL)
      .pipe(
        map((response: any) => response
          .map(merchant =>
            new Merchant(
              merchant.merchant_id,
              merchant.business_name,
              merchant.merchant_email,
              merchant.merchant_header_title,
              merchant.merchant_header_text)
          )
        ),
        share(),
      );
  }

  public getMerchantData(merchantId: string): Observable<Merchant> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${merchantId}/info`)
      .pipe(
        filter(Boolean),
        map((response: any) => dataToMerchantInfo(response, merchantId)),
        share(),
      );
  }

  public getMerchantByVanityLink(vanityLink: string): Observable<Merchant> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}${vanityLink}/info`)
      .pipe(
        filter(Boolean),
        map((response: any) => new Merchant(response.merchant_id, response.merchant_name, response.merchant_email, response.merchant_header_title, response.merchant_header_text)),
        share(),
      );
  }

  public getLoyaltyProgram(merchantId: string): Observable<LoyaltyProgram> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${merchantId}/loyalty-program`)
    .pipe(
      filter(Boolean),
      map((results: any) => dataToLoyaltyProgram(results)),
      share()
    );
  }

  public getMerchantMobileApp(merchantId: string): Observable<MerchantMobileApp> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${merchantId}/mobileapp`)
      .pipe(
        filter(Boolean),
        map((results: any) => dataToMerchantMobileAppInfo(results,merchantId)),
      );
  }

  public getMerchantGiftCardImages(merchantId: string): Observable<GiftCardImage[]> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}gift-cards/merchants/${merchantId}/images`)
      .pipe(
        filter(Boolean),
        map((results: any) => results.map(dataToMerchantGiftCardImage)),
      );
  }

  public getMobileAppHomepageTiles(merchantId: string): Observable<MobileAppHomepageTile[]> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${merchantId}/mobileapp/homepagetiles`)
      .pipe(
        filter(Boolean),
        map((results: any) => results.map(dataToMobileAppHomepageTile)),
      );
  }

  public getMobileAppMenuTabs(merchantId: string): Observable<MobileAppMenuTab[]> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${merchantId}/mobileapp/menutabs`)
      .pipe(
        filter(Boolean),
        map((results: any) => results.map(dataToMobileAppMenuTab)),
      );
  }

  public getMerchantCatalogItemIcons(merchantId: string): Observable<CatalogItemIcon[]> {
    return this.http.get(`${environment.apiUrl}merchants/${merchantId}/catalog/items/icons`)
    .pipe(
      filter(Boolean),
      map((results:any) => results.map(dataToCatalogItemIcon))
    );
  }

  public getMerchantNotifications(merchantId: string): Observable<Notification[]> {
    return this.http.get(`${environment.apiUrl}merchants/${merchantId}/mobileapp/notifications`)
    .pipe(
      filter(Boolean),
      map((results:any) => results.map(dataToNotification))
    );
  }

  public getMerchantPrivateSubscriptions(merchantId: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}merchants/${merchantId}/mobileapp/privatesubscriptions`)
    .pipe(
      map(results => Array.isArray(results) ? results : [])
    );
  }

  public getMerchantKyooPromos(merchantId: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}merchants/${merchantId}/kyoo-promos`)
    .pipe(
      map((results) => results.map(dataToPromos))
    );
  }

  /**
   * Combined bootstrap endpoint - replaces multiple API calls with single request
   * Returns all merchant data needed for app initialization
   */
  public getBootstrapData(merchantId: string): Observable<any> {
    const apiURL = environment.apiUrl;
    return this.http.get(`${apiURL}merchants/${merchantId}/bootstrap`)
      .pipe(
        filter(Boolean),
        share()
      );
  }
}
